import * as React from 'react';
import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';

import { AppState } from '..';
import { usersService } from '@services/UsersService';
import { ProfileViewModel } from '@models/Auth';

import forms from '@constants/Forms';

import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';
import { OptionsViewModel } from '@models/common.models';
import FormNames from '@constants/FormNames.enum';
import { authService } from '@services/AuthService';
import { requestAction } from '@common/components/common.actions';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum UserActionType {
  /********** Clear **********/
  ClearUserStart = 'ClearUserStart',
  ClearUserSuccess = 'ClearUserSuccess',

  /********** Create **********/
  CreateUserStart = 'CreateUserStart',
  CreateUserSuccess = 'CreateUserSuccess',
  CreateUserFailure = 'CreateUserFailure',

  /********** Get **********/
  GetUserStart = 'GetUserStart',
  GetUserSuccess = 'GetUserSuccess',
  GetUserFailure = 'GetUserFailure',
  GetRolesSuccess = 'GetRolesSuccess',
  GetRolesFailure = 'GetRolesFailure',

  /********** Update **********/
  UpdateUserStart = 'UpdateUserStart',
  UpdateUserSuccess = 'UpdateUserSuccess',
  UpdateUserFailure = 'UpdateUserFailure',
  ToggleRowOption = 'ToggleRowOption'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Clear **********/
export interface ClearUserStartAction extends Action {
  readonly type: UserActionType.ClearUserStart;
}

export interface ClearUserSuccessAction extends Action {
  readonly type: UserActionType.ClearUserSuccess;
}

/********** Create **********/
export interface CreateUserStartAction extends Action {
  readonly type: UserActionType.CreateUserStart;
}

export interface CreateUserSuccessAction extends Action {
  readonly id: number;
  readonly type: UserActionType.CreateUserSuccess;
}

export interface CreateUserFailureAction extends Action {
  readonly type: UserActionType.CreateUserFailure;
  readonly errorMessage: string;
}

/********** Get **********/
export interface GetUserStartAction extends Action {
  readonly type: UserActionType.GetUserStart;
}

export interface GetUserSuccessAction extends Action {
  readonly type: UserActionType.GetUserSuccess;
  readonly user: ProfileViewModel;
}

export interface GetUserFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: UserActionType.GetUserFailure;
}

export interface GetRolesSuccessAction extends Action {
  readonly type: UserActionType.GetRolesSuccess;
  readonly roleOptions: Array<OptionsViewModel>;
}

export interface GetRolesFailureAction extends Action {
  readonly errorMessage: string;
  readonly type: UserActionType.GetRolesFailure;
}

/********** Update **********/
export interface UpdateUserStartAction extends Action {
  readonly type: UserActionType.UpdateUserStart;
}

export interface UpdateUserSuccessAction extends Action {
  readonly type: UserActionType.UpdateUserSuccess;
}

export interface UpdateUserFailureAction extends Action {
  readonly type: UserActionType.UpdateUserFailure;
  readonly errorMessage: string;
}

export interface ToggleRowOptionAction extends Action {
  readonly type: UserActionType.ToggleRowOption;
  readonly option: OptionsViewModel;
}

// Actions for each action type declared here.
// They should all return an action object.

/********** Clear **********/
export const clearUser = () => {
  return (dispatch: Dispatch<AppState>) => {
    dispatch(clearUserStart());
    dispatch(clearUserSuccess());
    dispatch(actions.change(forms.UserForm, {}));
  };
};

export function clearUserStart(): ClearUserStartAction {
  return {
    type: UserActionType.ClearUserStart
  };
}

export function clearUserSuccess(): ClearUserSuccessAction {
  return {
    type: UserActionType.ClearUserSuccess
  };
}

/********** Create **********/
export const createUser = (model: ProfileViewModel) => {
  return async (dispatch: Dispatch<AppState>, getStore: () => AppState) => {
    dispatch(createUserStart());
    try {
      let id: number = await usersService.create(model);
      const storeState = getStore();
      var updateModel = {
        ...model,
        id: id,
        roleOptions: storeState.user.selectedRoleOptions
      } as ProfileViewModel;

      await dispatch(createUserSuccess(id));
      await usersService.update(updateModel);
    } catch (e) {
      await dispatch(createUserFailure(e.toString()));
    }
  };
};

export function createUserStart(): CreateUserStartAction {
  return {
    type: UserActionType.CreateUserStart
  };
}

export function createUserSuccess(id: number): CreateUserSuccessAction {
  toast.success(<ToastComponent type={ToastType.Success} message="User Created." />, ToastOptions.Success);

  return {
    id: id,
    type: UserActionType.CreateUserSuccess
  };
}

export function createUserFailure(errorMessage: string): CreateUserFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage.toString()} />, ToastOptions.Error);

  return {
    type: UserActionType.CreateUserFailure,
    errorMessage: errorMessage
  };
}

/********** Get **********/
export const getUser = (id?: number) => {
  return async (dispatch: Dispatch<AppState>) => {
    actions.reset(FormNames.UserForm);
    if (id) {
      dispatch(getUserStart());
      try {
        let user: ProfileViewModel = await usersService.get(id);
        dispatch(getUserSuccess(user));
        dispatch(actions.load(FormNames.UserForm, user));
      } catch (e) {
        dispatch(getUserFailure(e.toString()));
      }
    }
  };
};

export function getUserStart(): GetUserStartAction {
  return {
    type: UserActionType.GetUserStart
  };
}

export function getUserSuccess(user: ProfileViewModel): GetUserSuccessAction {
  return {
    type: UserActionType.GetUserSuccess,
    user: user
  };
}

export function getUserFailure(errorMessage: string): GetUserFailureAction {
  return {
    type: UserActionType.GetUserFailure,
    errorMessage: errorMessage
  };
}

export const getRoleOptions = () => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getRolesStart());
    try {
      dispatch(requestAction(() => authService.getRoleOptions(), getRolesSuccess));
    } catch (e) {
      dispatch(getRolesFailure(e.toString()));
    }
  };
};

export function getRolesStart(): GetUserStartAction {
  return {
    type: UserActionType.GetUserStart
  };
}

export function getRolesSuccess(roleOptions: Array<OptionsViewModel>): GetRolesSuccessAction {
  return {
    type: UserActionType.GetRolesSuccess,
    roleOptions: roleOptions
  };
}

export function getRolesFailure(errorMessage: string): GetRolesFailureAction {
  return {
    type: UserActionType.GetRolesFailure,
    errorMessage: errorMessage
  };
}

/********** Update **********/
export const updateUser = (model: ProfileViewModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(updateUserStart());
    try {
      await usersService.update(model);
      dispatch(updateUserSuccess());
    } catch (e) {
      dispatch(updateUserFailure(e.toString()));
    }
  };
};

export function updateUserStart(): UpdateUserStartAction {
  return {
    type: UserActionType.UpdateUserStart
  };
}

export function updateUserSuccess(): UpdateUserSuccessAction {
  toast.success(<ToastComponent type={ToastType.Success} message="User Updated." />, ToastOptions.Success);

  return {
    type: UserActionType.UpdateUserSuccess
  };
}

export function updateUserFailure(errorMessage: string): UpdateUserFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={errorMessage.toString()} />, ToastOptions.Error);

  return {
    type: UserActionType.UpdateUserFailure,
    errorMessage: errorMessage
  };
}

export function toggleRowOption(roleOption: OptionsViewModel): ToggleRowOptionAction {
  return {
    type: UserActionType.ToggleRowOption,
    option: roleOption
  };
}

export const loadUserForm = (user: ProfileViewModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    actions.reset(FormNames.UserForm);
    if (user.id) {
      dispatch(actions.load(FormNames.UserForm, user));
    }
  };
};



// WEBPACK FOOTER //
// ./src/user/user.actions.tsx